import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { houzyContact } from '../../../lib/houzyContact';

// Squidex holds up to this many FAQ slots (SETTINGS.SUPPORT.FAQ.Q1..Q18); unused
// slots are left empty in Squidex and filtered out below.
const SUPPORT_FAQ_QUESTION_COUNT = 18;

type FaqItem = { questionNumber: number; title: string; body: string };

export function SupportSettingsPage(): ReactElement {
    const { t } = useTranslation();
    const headingId = useId();

    const faqItems: FaqItem[] = [];
    for (let questionNumber = 1; questionNumber <= SUPPORT_FAQ_QUESTION_COUNT; questionNumber++) {
        const titleKey = `SETTINGS.SUPPORT.FAQ.Q${questionNumber}.TITLE`;
        const bodyKey = `SETTINGS.SUPPORT.FAQ.Q${questionNumber}.BODY`;
        const rawTitle = t(titleKey);
        const rawBody = t(bodyKey);
        // With returnEmptyString:false, t() falls back to returning the key itself both
        // when the key is missing and when its Squidex value is empty. A whitespace-only
        // Squidex value (used as a placeholder to satisfy Squidex's translate-on-create
        // requirement) is treated the same way via trim().
        const title = rawTitle === titleKey ? '' : rawTitle.trim();
        const body = rawBody === bodyKey ? '' : rawBody.trim();
        if (title && body) {
            faqItems.push({ questionNumber, title, body });
        }
    }

    return (
        <Stack spacing={{ xs: 2, md: 3 }}>
            <Card variant="outlined" component="section" aria-labelledby={headingId}>
                <CardHeader
                    title={t('SETTINGS.SUPPORT.TITLE')}
                    slotProps={{ title: { variant: 'h6', component: 'h2', id: headingId } }}
                />
                <CardContent sx={{ pt: 0 }}>
                    <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                            {t('SETTINGS.SUPPORT.SUBTITLE')}
                        </Typography>

                        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                            <Card variant="outlined" sx={{ flex: '1 1 220px' }}>
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <PhoneOutlinedIcon fontSize="small" color="primary" />
                                        <Typography variant="subtitle2">{t('SETTINGS.SUPPORT.PHONE.TITLE')}</Typography>
                                    </Stack>
                                    <Link
                                        href={houzyContact.partnerManagementPhoneHref}
                                        variant="body2"
                                        underline="none"
                                        sx={{ display: 'inline-block', mt: 1, fontWeight: 600 }}>
                                        {houzyContact.partnerManagementPhoneNumber}
                                    </Link>
                                    <Typography variant="caption" color="text.secondary" component="p">
                                        {t('SETTINGS.SUPPORT.PHONE.AVAILABILITY')}
                                    </Typography>
                                </CardContent>
                            </Card>

                            <Card variant="outlined" sx={{ flex: '1 1 220px' }}>
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <EmailOutlinedIcon fontSize="small" color="primary" />
                                        <Typography variant="subtitle2">{t('SETTINGS.SUPPORT.EMAIL.TITLE')}</Typography>
                                    </Stack>
                                    <Link
                                        href={`mailto:${houzyContact.supportEmail}`}
                                        variant="body2"
                                        underline="none"
                                        sx={{ display: 'inline-block', mt: 1, fontWeight: 600 }}>
                                        {houzyContact.supportEmail}
                                    </Link>
                                    <Typography variant="caption" color="text.secondary" component="p">
                                        {t('SETTINGS.SUPPORT.EMAIL.AVAILABILITY')}
                                    </Typography>
                                </CardContent>
                            </Card>

                            <Card variant="outlined" sx={{ flex: '1 1 220px' }}>
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <AccessTimeOutlinedIcon fontSize="small" color="primary" />
                                        <Typography variant="subtitle2">{t('SETTINGS.SUPPORT.HOURS.TITLE')}</Typography>
                                    </Stack>
                                    <Box sx={{ mt: 1 }}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2">
                                                {t('SETTINGS.SUPPORT.HOURS.MON_FRI')}
                                            </Typography>
                                            <Typography variant="body2">08:00 - 18:00</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2">
                                                {t('SETTINGS.SUPPORT.HOURS.SAT_SUN')}
                                            </Typography>
                                            <Typography variant="body2">
                                                {t('SETTINGS.SUPPORT.HOURS.CLOSED')}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Stack>

                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                {t('SETTINGS.SUPPORT.FAQ.TITLE')}
                            </Typography>
                            <Stack spacing={1}>
                                {faqItems.map(({ questionNumber, title, body }) => (
                                    <Card key={questionNumber} variant="outlined">
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Typography variant="subtitle2">{title}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {body}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    );
}
